import { formatDateTimeString, formatDistanceToNow, type WithClassName } from "@regrello/core-utils";
import {
  type RegrelloDatePickerProps,
  RegrelloDateTimePicker,
  type RegrelloDateTimePickerProps,
  type RegrelloIntent,
  type RegrelloSize,
} from "@regrello/ui-core";
import React from "react";
import type { RegrelloFormFieldBaseProps } from "./_internal/RegrelloFormFieldBaseProps";
import { RegrelloFormFieldEmptyValueNone } from "./_internal/RegrelloFormFieldEmptyValueNone";
import { RegrelloFormFieldLayout } from "./_internal/RegrelloFormFieldLayout";
import { getSelfContainedFormInternal } from "./_internal/selfContainedForm/getSelfContainedFormInternal";

export interface RegrelloFormFieldDatePropsV2
  extends WithClassName,
    RegrelloFormFieldBaseProps<Date | null>,
    Pick<
      RegrelloDatePickerProps,
      | "autoFocus"
      | "enableDynamicCalendarPicker"
      | "firstSegmentInitialKeystroke"
      | "firstSegmentRef"
      | "getIsDateDisabled"
      | "inputRef"
      | "onClosePopover"
      | "onKeyDownCapture"
      | "overrideFocusGroupId"
    >,
    Pick<RegrelloDateTimePickerProps, "isDefaultTimeStartOfDay" | "ignoreTimezone" | "onBlur"> {
  /**
   * Whether to set the date picker to full width. If the time picker is visible (via
   * {@see showTimePicker}), then only the date input will stretch to fill available space.
   *
   * @default false
   */
  fullWidth?: boolean;

  /**
   * Apply the disabled modifier to the matching days.
   */
  getIsDateDisabled?: (day: Date) => boolean;

  /**
   * The intent to apply to this icon.
   *
   * @default undefined
   */
  infoTooltipIntent?: RegrelloIntent;

  /** Additional class name for the date picker's input element. */
  inputClassName?: string;

  /**
   * Whether to hide the form field error icon and message. Added (potentially temporarily depending
   * on Design System updates) for Conditional Branching to support the new error state for stage
   * start date forms.
   */
  isErrorMessageHidden?: boolean;

  /**
   * Maximal selectable date
   */
  maxDate?: Date;

  /**
   * Minimal selectable date
   */
  minDate?: Date;

  onChange: (date: Date | string | null, value?: string | null) => void;

  /**
   * Whether to show a relative-difference phrase ("in a moment", "in 2 days", etc.) after the
   * display value in the `selfContainedForm`.
   *
   * @default false
   */
  showRelativeDiffForSelfContainedFormValue?: boolean;

  /**
   * Whether to show a time-picker with the date-picker.
   *
   * @default false
   */
  showTimePicker?: boolean;

  /**
   * The size of the input field.
   *
   * @default RegrelloSize.LARGE
   */
  size?: RegrelloSize;

  value?: Date | null;

  /**
   * Should chosen value be represented as string instead of `Date`
   */
  valueAsString?: boolean;
}

/**
 * Component that can be used in forms to select a date.
 * Supports both accessible segmented input with keyboard navigation and calendar popover.
 */
export const RegrelloFormFieldDate = React.memo<RegrelloFormFieldDatePropsV2>(function RegrelloFormFieldDateFn({
  autoFocus = false,
  className,
  dataTestId,
  disabled,
  enableDynamicCalendarPicker,
  endAdornment,
  error,
  fullWidth = false,
  firstSegmentInitialKeystroke,
  firstSegmentRef,
  getIsDateDisabled,
  helperText,
  infoTooltipText,
  infoTooltipIconName,
  infoTooltipVariant,
  infoTooltipIntent,
  inputClassName,
  inputRef,
  isDefaultMarginsOmitted,
  isDefaultTimeStartOfDay,
  isDeleted,
  isEmphasized,
  isErrorMessageHidden = false,
  isLabelVerticallyCentered,
  isRequiredAsteriskShown,
  label,
  labelPlacement,
  labelWidth,
  maxDate,
  minDate,
  onBlur,
  onChange,
  onKeyDownCapture,
  onClosePopover,
  overrideFocusGroupId,
  ignoreTimezone = false,
  selfContainedForm,
  showRelativeDiffForSelfContainedFormValue = false,
  showTimePicker = false,
  size,
  value,
  valueAsString = false,
  variant = "default",
  warning,
}) {
  const getSavedValueContent = (savedValue: Date | null) => {
    switch (true) {
      case savedValue === null:
        return <RegrelloFormFieldEmptyValueNone />;
      default:
        return showRelativeDiffForSelfContainedFormValue ? (
          <span>
            {formatDateTimeString(savedValue)}{" "}
            <span className="text-textMuted">({formatDistanceToNow(savedValue.toISOString())})</span>
          </span>
        ) : (
          formatDateTimeString(savedValue)
        );
    }
  };

  const intent: RegrelloDatePickerProps["intent"] = error != null ? "danger" : isEmphasized ? "warning" : undefined;

  const datePickerProps: NonNullable<RegrelloDateTimePickerProps["datePickerProps"]> = {
    autoFocus,
    className: inputClassName,
    enableDynamicCalendarPicker,
    firstSegmentInitialKeystroke,
    firstSegmentRef,
    getIsDateDisabled,
    inputRef,
    onClosePopover,
    overrideFocusGroupId,
    intent,
    onKeyDownCapture,
  };

  return (
    <RegrelloFormFieldLayout
      className={className}
      dataTestId={dataTestId}
      endAdornment={endAdornment}
      error={isErrorMessageHidden ? undefined : error}
      helperText={helperText}
      infoTooltipIconName={infoTooltipIconName}
      infoTooltipIntent={infoTooltipIntent}
      infoTooltipText={infoTooltipText}
      infoTooltipVariant={infoTooltipVariant}
      isDefaultMarginsOmitted={isDefaultMarginsOmitted}
      isDeleted={isDeleted}
      isLabelVerticallyCentered={isLabelVerticallyCentered}
      isRequiredAsteriskShown={isRequiredAsteriskShown}
      label={label}
      labelPlacement={labelPlacement}
      labelWidth={labelWidth}
      selfContainedForm={getSelfContainedFormInternal(selfContainedForm, (savedValue) =>
        getSavedValueContent(savedValue),
      )}
      variant={variant}
      warning={warning}
    >
      <RegrelloDateTimePicker
        datePickerProps={datePickerProps}
        disabled={disabled}
        fullWidth={fullWidth}
        ignoreTimezone={ignoreTimezone}
        isDefaultTimeStartOfDay={isDefaultTimeStartOfDay}
        maxValue={maxDate}
        minValue={minDate}
        onBlur={onBlur}
        onChange={onChange}
        showTimePicker={showTimePicker}
        size={size}
        timePickerProps={{ intent }}
        value={value}
        valueAsString={valueAsString}
      />
    </RegrelloFormFieldLayout>
  );
});
